import { useEffect, useState } from 'react';

export const useLocalStorage = (
    key: string,
): {
    localStorageItems: any[];
    addLocalStorageItem: (
        uniqueIdentifier: string,
        maxItemCount?: number,
    ) => void;
    removeLocalStorageItem: (uniqueIdentifier: string) => void;
} => {
    const [localStorageItems, setLocalStorageItems] = useState<any[]>([]);

    useEffect(() => {
        try {
            const localStorageItems = localStorage?.getItem(key);

            if (localStorageItems) {
                setLocalStorageItems(JSON?.parse(localStorageItems));
            }
        } catch {
            setLocalStorageItems([]);
        }
    }, [key]);

    const removeLocalStorageItem = (uniqueIdentifier: string): void => {
        try {
            const localStorageItemsCopy = [...localStorageItems];

            const itemsFiltered = localStorageItemsCopy?.filter(
                (item) => item !== uniqueIdentifier,
            );

            localStorage?.setItem(key, JSON?.stringify(itemsFiltered));

            setLocalStorageItems(itemsFiltered);
        } catch {
            setLocalStorageItems([]);
        }
    };

    const saveLocalStorageItem = (
        uniqueIdentifier: string,
        items: any[],
    ): void => {
        try {
            items?.unshift(uniqueIdentifier);

            localStorage?.setItem(key, JSON?.stringify(items));

            setLocalStorageItems(items);
        } catch {
            setLocalStorageItems([]);
        }
    };

    const saveDuplicateLocalStorageItem = (
        uniqueIdentifier: string,
        localStorageItems: any[],
    ): void => {
        try {
            const localStorageItemsFiltered = localStorageItems?.filter(
                (item) => item !== uniqueIdentifier,
            );

            saveLocalStorageItem(uniqueIdentifier, localStorageItemsFiltered);
        } catch {
            setLocalStorageItems([]);
        }
    };

    const saveLocalStorageItemWhenMaxReached = (
        uniqueIdentifier: string,
        maxItemCount: number,
        localStorageItems: any[],
    ): void => {
        try {
            const localStorageItemsFiltered = localStorageItems?.filter(
                (item) => item !== localStorageItems[maxItemCount - 1],
            );

            saveLocalStorageItem(uniqueIdentifier, localStorageItemsFiltered);
        } catch {
            setLocalStorageItems([]);
        }
    };

    const addLocalStorageItem = (
        uniqueIdentifier: string,
        maxItemCount?: number,
    ): void => {
        try {
            const localStorageItemsCopy = [...localStorageItems];

            const localStorageItemAlreadyDetected = localStorageItemsCopy?.find(
                (item) => item === uniqueIdentifier,
            );

            if (!localStorageItemAlreadyDetected) {
                if (maxItemCount) {
                    if (localStorageItemsCopy?.length >= maxItemCount) {
                        saveLocalStorageItemWhenMaxReached(
                            uniqueIdentifier,
                            maxItemCount,
                            localStorageItemsCopy,
                        );
                    } else {
                        saveLocalStorageItem(
                            uniqueIdentifier,
                            localStorageItemsCopy,
                        );
                    }
                } else {
                    saveLocalStorageItem(
                        uniqueIdentifier,
                        localStorageItemsCopy,
                    );
                }
            } else {
                saveDuplicateLocalStorageItem(
                    uniqueIdentifier,
                    localStorageItemsCopy,
                );
            }
        } catch {
            setLocalStorageItems([]);
        }
    };

    return { localStorageItems, addLocalStorageItem, removeLocalStorageItem };
};
